import { useState, useEffect } from 'react'
import { X, Users, Plus, CheckCircle, AlertCircle } from 'lucide-react'
import { apiClient } from '@/lib/api'

interface Candidate {
  id: number
  name: string
  firstName: string
  lastName: string
  email: string
  phone: string
  currentTitle: string
  currentCompany: string
  location: string
  experience: number
  skills: string[]
  status: string
}

interface AssociateCandidateModalProps {
  open: boolean
  onClose: () => void
  position: any
  onAssociated?: () => void
}

export default function AssociateCandidateModal({ 
  open, 
  onClose, 
  position, 
  onAssociated 
}: AssociateCandidateModalProps) {
  const [candidates, setCandidates] = useState<Candidate[]>([])
  const [selectedCandidates, setSelectedCandidates] = useState<Array<{candidateId: number, event: string}>>([])
  const [loading, setLoading] = useState(false)
  const [fetchingCandidates, setFetchingCandidates] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // Fetch available candidates when modal opens
  useEffect(() => {
    if (open && position?.id) {
      fetchAvailableCandidates()
    }
  }, [open, position?.id])

  const fetchAvailableCandidates = async () => {
    try {
      setFetchingCandidates(true)
      setError('')
      
      const response = await apiClient.request(`/positions/available-candidates/${position.id}`)
      
      if (response.success) {
        setCandidates((response.data as any).candidates || [])
      } else {
        setError('Failed to fetch available candidates')
      }
    } catch (error: any) {
      console.error('Error fetching candidates:', error)
      setError(error.message || 'Failed to fetch available candidates')
    } finally {
      setFetchingCandidates(false)
    }
  }

  const resetForm = () => {
    setSelectedCandidates([])
    setError('')
    setSuccess('')
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const handleCandidateToggle = (candidateId: number) => {
    setSelectedCandidates(prev => {
      const prevArray = prev || []
      const exists = prevArray.find(c => c.candidateId === candidateId)
      if (exists) {
        return prevArray.filter(c => c.candidateId !== candidateId)
      } else {
        return [...prevArray, { candidateId, event: 'shortlisted' }]
      }
    })
  }


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!position?.id || (selectedCandidates?.length || 0) === 0) return

    try {
      setLoading(true)
      setError('')
      setSuccess('')
      
      const response = await apiClient.request(`/positions/associate-candidates/${position.id}`, {
        method: 'POST',
        body: JSON.stringify({
          candidates: selectedCandidates
        })
      })

      if (response.success) {
        setSuccess(`Successfully associated position with ${(response.data as any).associations_created} candidate(s)`)
        setTimeout(() => {
          handleClose()
          onAssociated?.()
        }, 1500)
      } else {
        setError((response.error as any)?.message || 'Failed to associate candidates')
      }
    } catch (error: any) {
      console.error('Error associating candidates:', error)
      setError(error.message || 'Failed to associate candidates')
    } finally {
      setLoading(false)
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center">
            <Users className="w-5 h-5 text-blue-600 mr-2" />
            <h2 className="text-xl font-semibold text-gray-900">
              Associate Candidates
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex flex-col h-full max-h-[calc(90vh-120px)]">
          {/* Fixed header section */}
          <div className="p-6 flex-shrink-0">
            {position && (
              <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                <h3 className="font-medium text-blue-900">{position.title}</h3>
                <p className="text-blue-700 text-sm">{position.location} • {position.department}</p>
              </div>
            )}

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center">
                <AlertCircle className="w-4 h-4 text-red-500 mr-2 flex-shrink-0" />
                <span className="text-red-700 text-sm">{error}</span>
              </div>
            )}

            {success && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center">
                <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                <span className="text-green-700 text-sm">{success}</span>
              </div>
            )}

            <h4 className="font-medium text-gray-900 mb-4">Available Candidates</h4>
          </div>

          {/* Scrollable candidates list */}
          <div className="flex-1 overflow-y-auto px-6">
            {fetchingCandidates ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-500 mt-2">Loading candidates...</p>
              </div>
            ) : candidates.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <h4 className="text-sm font-medium text-gray-900 mb-2">No Available Candidates</h4>
                <p className="text-sm text-gray-600">All candidates are already associated with this position.</p>
              </div>
            ) : (
              <div className="space-y-3 pb-4">
                {candidates.map((candidate) => {
                  const isSelected = selectedCandidates?.some(c => c.candidateId === candidate.id) || false
                  const selectedCandidate = selectedCandidates?.find(c => c.candidateId === candidate.id)
                  
                  return (
                    <div key={candidate.id} className={`border rounded-lg p-4 ${isSelected ? 'border-blue-300 bg-blue-50' : 'border-gray-200'}`}>
                      <div className="flex items-start">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleCandidateToggle(candidate.id)}
                          className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <div className="ml-3 flex-1">
                          <div className="flex items-start justify-between">
                            <div>
                              <h5 className="font-medium text-gray-900">{candidate.name}</h5>
                              <p className="text-sm text-gray-600">{candidate.email}</p>
                              <p className="text-sm text-gray-500">
                                {candidate.currentTitle && `${candidate.currentTitle}`}
                                {candidate.currentCompany && ` at ${candidate.currentCompany}`}
                              </p>
                              <p className="text-sm text-gray-500">
                                {candidate.location} • {candidate.experience} years exp
                              </p>
                              {candidate.skills.length > 0 && (
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {candidate.skills.slice(0, 3).map((skill, index) => {
                                    const skillName = typeof skill === 'string' 
                                      ? skill 
                                      : (skill as any).skillName || (skill as any).skill_name || (skill as any).name;
                                    return (
                                      <span key={(skill as any).uuid || index} className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                                        {skillName}
                                      </span>
                                    );
                                  })}
                                  {candidate.skills.length > 3 && (
                                    <span className="px-2 py-1 text-xs text-gray-500">
                                      +{candidate.skills.length - 3} more
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              candidate.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {candidate.status}
                            </span>
                          </div>
                          
                          {isSelected && (
                            <div className="mt-3">
                              <div className="px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                                <span className="text-sm font-medium text-blue-700">
                                  Will be added as: Shortlisted
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Fixed footer with buttons */}
          <form onSubmit={handleSubmit} className="flex-shrink-0">
            <div className="flex items-center justify-between p-6 border-t bg-gray-50">
              <div className="text-sm text-gray-600">
                {selectedCandidates?.length || 0} candidate{(selectedCandidates?.length || 0) !== 1 ? 's' : ''} selected
              </div>
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || (selectedCandidates?.length || 0) === 0}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Associating...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Associate Candidates
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}